import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isNil } from 'ramda';
import {
  currentProject,
  selectCurrentBuilding,
} from 'spw-redux/modules/projects/selectors';
import { Grid } from '@mui/material';
import { WithoutBuildingMessage } from 'spw-esa-projects/program-summary/styles';
import { IFloor } from 'spw-redux/modules/projects/types';
import {
  actions,
  ID_TYPE,
  setSelectedLevels,
  updateSpacesByType,
} from 'spw-redux/modules/program-summary/actions';
import {
  getSpaceTypeCategories,
  setCurrentBuilding,
  setCurrentFloor,
} from 'spw-redux/modules/projects/actions';
import {
  ProgramSummaryParams,
  UpdateSpacesByTypeParams,
} from 'spw-redux/modules/program-summary/types';
import SummaryCards from 'spw-esa-projects/program-summary/summary-cards';
import {
  currentSelectedType,
  selectKeyData,
  selectSelectedLevels,
} from 'spw-redux/modules/program-summary/esa/selectors';
import { useLocation } from 'react-router-v5';
import { useProgramSummary } from 'spw-esa-projects/hooks/hooks';
import Header from 'spw-esa-projects/program-summary/header';

type Props = {
  isRenderingForExport?: boolean;
  downloadParams?: {
    showRsf?: boolean;
    headcountType?: string;
    clientFacingNamesAreVisible?: boolean;
  };
};

export const ProgramSummary = ({
  isRenderingForExport,
  downloadParams,
}: Props) => {
  const dispatch = useDispatch();
  const [showRsf, toggleShowRsf] = useState(downloadParams?.showRsf || false);
  const location = useLocation();
  const searchParams = useRef(new URLSearchParams(location.search));
  const floorId = searchParams.current.get('floor');
  const buildingId = searchParams.current.get('building');

  const project = useSelector(currentProject);
  const building = useSelector(selectCurrentBuilding);
  const selectedType = useSelector(currentSelectedType);
  const keyData = useSelector(selectKeyData);
  const selectedLevels = useSelector(selectSelectedLevels);
  const [isLoadingKeyData, setIsLoadingKeyData] = useState(false);
  const [initialSelection, setInitialSelection] = useState<
    ProgramSummaryParams
  >({
    floorIds: [],
    buildingIds: [],
  });

  const { getSummaryData } = useProgramSummary();

  useEffect(() => {
    if (
      selectedLevels.floorIds.length > 0 ||
      selectedLevels.buildingIds.length > 0
    ) {
      getSummaryData(selectedLevels);
    }
  }, [selectedLevels, getSummaryData]);

  useEffect(() => {
    if (!buildingId && !building?.id && project?.buildings?.length) {
      // Select first building
      dispatch(setCurrentBuilding(project.buildings[0]));
    }
  }, [buildingId, building?.id, project?.buildings, dispatch]);

  useEffect(() => {
    if (floorId) {
      const currentFloorId = parseInt(floorId, 10);
      const floor = building?.floors?.find(
        (buildingFloor: IFloor) => buildingFloor.id === currentFloorId
      );
      if (floor) {
        dispatch(setCurrentFloor(floor));
        setIsLoadingKeyData(true);
        setInitialSelection({ floorIds: [floor.id], buildingIds: [] });
      }
    } else if (building?.id) {
      const { id } = building;
      setIsLoadingKeyData(true);
      dispatch(actions.setSummarySelectedIdType(id, ID_TYPE.buildingId));
      dispatch(getSpaceTypeCategories());
      setInitialSelection({ floorIds: [], buildingIds: [id] });
    }
  }, [floorId, selectedType, building, dispatch]);

  useEffect(() => {
    if (isRenderingForExport) {
      const selectedFloorIds = searchParams.current.get('floor_ids');
      const selectedBuildingIds = searchParams.current.get('building_ids');
      setIsLoadingKeyData(true);
      dispatch(
        setSelectedLevels({
          floorIds: selectedFloorIds
            ? selectedFloorIds.split(',')?.map((id) => parseInt(id, 10))
            : [],
          buildingIds: selectedBuildingIds
            ? selectedBuildingIds.split(',')?.map((id) => parseInt(id, 10))
            : [],
        })
      );
    }
  }, [isRenderingForExport, dispatch]);

  useEffect(() => {
    if (!isNil(keyData)) {
      setIsLoadingKeyData(false);
    }
  }, [keyData]);

  const handleUpdateAvg = (payload: UpdateSpacesByTypeParams) => {
    dispatch(
      updateSpacesByType({
        ...payload,
        callback: () => {
          getSummaryData(selectedLevels);
          payload.callback();
        },
      })
    );
  };

  if (!building) {
    return <WithoutBuildingMessage>No buildings added</WithoutBuildingMessage>;
  }

  return (
    <Grid container>
      <Grid item xs={12}>
        <Grid
          container
          direction="row"
          justifyContent="flex-start"
          alignItems="flex-start"
          spacing={isRenderingForExport ? 0 : 2}
        >
          <Grid item xs={12}>
            {!isRenderingForExport && (
              <Header
                initialSelection={initialSelection}
                showRsf={showRsf}
                toggleShowRsf={toggleShowRsf}
              />
            )}
            {!isLoadingKeyData && (
              <SummaryCards
                isRenderingForExport={isRenderingForExport}
                showRsfData={showRsf}
                onUpdateAvg={handleUpdateAvg}
                clientFacingNamesAreVisible={
                  downloadParams.clientFacingNamesAreVisible
                }
              />
            )}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
